import PropTypes from 'prop-types';
import { useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { toast } from 'react-toastify';
import AppScrollArea from '../../../../components/common/AppScrollArea';
import EventModalMediaContent from '../../../../components/EventModalMediaContent';
import SubtleBadge from '../../../../components/common/SubtleBadge';
import ApprovalRejectReasonModal from '../../../../components/modal/ApprovalRejectReasonModal';
import OrderApprovalHistory from '../../../order/OrderApprovalHistory';
import OrderDetailModal from '../../../order/modal/OrderDetailModal';
import { useOrderContext } from '../../../../providers/OrderProvider';
import FinalCostReapplyModal from './FinalCostReapplyModal';
import { useCancelCustomerApplicationMutation, useGetCustomerApprovalHistoryQuery, useUpdateOrderApprovalByIdMutation } from '../../../../slices/approvalsApiSlice';
import { useNotifyOrderApprovedToApplicantMutation } from '../../../../slices/notificationsApiSlice';
import { useApplyApprovedFinalCostAdjustmentMutation } from '../../../../slices/ordersApiSlice';
import { hasPermission, PERM } from '../../../../utils/accessPermissions';
import formatCurrency from '../../../../utils/formatCurrency';
import { getActualCreatorHighlight } from '../../../../utils/approval/approvalListHub';

const STATUS_META = {
	waitingApproval: { bg: 'warning', text: '待审批' },
	reject: { bg: 'danger', text: '已驳回' },
	approved: { bg: 'success', text: '已通过' },
	withdrawn: { bg: 'secondary', text: '已撤回' },
};

const HISTORY_ASIDE_SCROLL_MAX = 'min(24rem, calc(90vh - 14rem))';

const formatDateTime = (date) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

/** 待办里查看最终成本修正：审批侧可同意/不同意 */
const FinalCostApplyInfoModal = ({ show, onHide, approval, onResolved, allowDecision = true, allowApplicantActions = true }) => {
	const { isDark } = useSelector((state) => state.config);
	const { userInfo } = useSelector((state) => state.auth);
	const { setCurrentOrderOIdState } = useOrderContext();
	const canExecute = hasPermission(userInfo, PERM.APPROVAL_FINAL_COST_EXECUTE);
	const canAdjust = hasPermission(userInfo, PERM.ORDER_FINAL_COST_ADJUST);
	const [updateOrderApprovalStatus] = useUpdateOrderApprovalByIdMutation();
	const [applyApprovedAdjustment] = useApplyApprovedFinalCostAdjustmentMutation();
	const [notifyOrderApprovedToApplicant] = useNotifyOrderApprovedToApplicantMutation();
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);
	const [showRejectConfirm, setShowRejectConfirm] = useState(false);
	const [showReapply, setShowReapply] = useState(false);
	const [showOrderDetail, setShowOrderDetail] = useState(false);
	const [cancelApplication, { isLoading: isCancelling }] = useCancelCustomerApplicationMutation();
	const detail = approval?.detail || {};
	const statusMeta = STATUS_META[approval?.status] || { bg: 'secondary', text: approval?.status || '成本修正' };
	const isWaiting = approval?.status === 'waitingApproval';
	const isRejected = approval?.status === 'reject';
	const canDecide = allowDecision && isWaiting && canExecute;
	const orderId = detail.orderId || '';
	const orderOID = detail.orderOID ? String(detail.orderOID) : '';
	const title = orderId ? `订单 ${orderId} 最终成本修正` : '最终成本修正';
	const applicantName = approval?.applicant?.name || '';
	const assistantName = approval?.assistant?.name || '';
	const creatorHighlight = getActualCreatorHighlight({
		entryKind: 'finalCost',
		applicant: approval?.applicant,
		assistant: approval?.assistant,
	});
	const approvalId = approval?._id;
	const {
		data: fetchedHistory,
		isLoading: isHistoryLoading,
		isFetching: isHistoryFetching,
		isError: isHistoryError,
	} = useGetCustomerApprovalHistoryQuery(approvalId, {
		skip: !show || !approvalId,
	});
	const historyLoading = Boolean(show && approvalId && (isHistoryLoading || isHistoryFetching) && !isHistoryError);
	const history = Array.isArray(fetchedHistory) ? fetchedHistory : [];
	const previousCost = Number(detail.previousCost) || 0;
	const adjustedCost = Number(detail.adjustedCost) || 0;
	const change = adjustedCost - previousCost;
	const actionBusy = Boolean(isApproving || isRejecting || isCancelling);

	const handleViewOrder = () => {
		if (!orderOID || actionBusy) return;
		setCurrentOrderOIdState(orderOID);
		setShowOrderDetail(true);
	};

	const handleCancelApplication = async () => {
		if (!approvalId || actionBusy) return;
		try {
			await cancelApplication(approvalId).unwrap();
			toast.success('已取消申请，不再出现在被驳回待办');
			onResolved?.({ approvalId, orderId: detail.orderId || '', result: 'cancelled' });
			onHide();
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '取消申请失败，请重试');
		}
	};

	const notifyParties = async (approvedRes, passed) => {
		const { approver, applicant, assistant, detail, _id } = approvedRes;
		const notice = {
			fromUser: approver,
			content: {
				message_text: `订单 ${detail?.orderId || ''} 最终成本修正${passed ? '已通过' : '未通过'}`,
				emoji: passed ? '✅' : '❌',
				detail,
				approvalId: _id,
			},
		};
		const notices = [notifyOrderApprovedToApplicant({ ...notice, toUser: applicant })];
		if (assistant) notices.push(notifyOrderApprovedToApplicant({ ...notice, toUser: assistant }));
		await Promise.all(notices);
	};

	const handleApprove = async () => {
		const approvalId = approval?._id;
		if (!approvalId || actionBusy) return;
		setIsApproving(true);
		try {
			const approved = await updateOrderApprovalStatus({ _id: approvalId, status: 'approved' }).unwrap();
			const { detail, _id } = approved;
			await applyApprovedAdjustment({
				orderId: detail?.orderOID,
				approvalId: _id,
			}).unwrap();
			await notifyParties(approved, true);
			toast.success('审批通过，最终成本修正已应用');
			onResolved?.({ approvalId, orderId: detail?.orderId || '', result: 'approved' });
			onHide();
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '审批操作失败，请稍后重试');
		} finally {
			setIsApproving(false);
		}
	};

	const handleReject = async (rejectReason) => {
		const approvalId = approval?._id;
		const approvalSuggestions = String(rejectReason || '').trim();
		if (!approvalId || actionBusy) return;
		if (!approvalSuggestions) {
			toast.error('请填写驳回原因');
			return;
		}
		setIsRejecting(true);
		try {
			const rejected = await updateOrderApprovalStatus({
				_id: approvalId,
				status: 'reject',
				approvalSuggestions,
			}).unwrap();
			await notifyParties(rejected, false);
			toast.success('申请已驳回');
			setShowRejectConfirm(false);
			onResolved?.({ approvalId, orderId: rejected?.detail?.orderId || '', result: 'rejected' });
			onHide();
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '驳回操作失败，请稍后重试');
		} finally {
			setIsRejecting(false);
		}
	};

	return (
		<Modal show={show} onHide={actionBusy ? undefined : onHide} contentClassName='border' centered size='lg' backdrop={actionBusy ? 'static' : true} keyboard={!actionBusy}>
			<Modal.Header closeButton={!actionBusy} closeVariant={isDark ? 'white' : undefined} className='bg-body-tertiary px-x1 border-bottom-0'>
				<Modal.Title className='min-w-0'>
					<h5 className='mb-0 d-flex align-items-center gap-2 flex-nowrap min-w-0'>
						<span className='text-truncate' title={title}>
							{title}
						</span>
						<SubtleBadge bg={statusMeta.bg} pill className='flex-shrink-0'>
							{statusMeta.text}
						</SubtleBadge>
					</h5>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className='p-0 fs-10'>
				<div className='row g-0 align-items-stretch'>
					<div className='col-12 col-md min-w-0 px-x1 pb-x1 pt-1'>
						<EventModalMediaContent icon='sack-dollar' heading='修正信息'>
							<div className='fs-10'>
								<p className='mb-2'>
									<strong>订单编号：</strong>
									{orderId || '-'}
								</p>
								<p className='mb-2'>
									<strong>修正前：</strong>
									{formatCurrency(previousCost)}
								</p>
								<p className='mb-2'>
									<strong>修正后：</strong>
									{formatCurrency(adjustedCost)}
								</p>
								<p className='mb-2'>
									<strong>变化：</strong>
									{change > 0 ? '+' : ''}
									{formatCurrency(change)}
								</p>
								<p className='mb-0'>
									<strong>修正原因：</strong>
									{detail.reason || '-'}
								</p>
							</div>
						</EventModalMediaContent>

						<EventModalMediaContent icon='user' heading='申请人'>
							<div className='fs-10'>
								<p className='mb-2'>
									<strong>申请人：</strong>
									<span className={creatorHighlight.applicant ? 'text-primary' : undefined}>{applicantName || '-'}</span>
								</p>
								<p className='mb-2'>
									<strong>销售助理：</strong>
									<span className={creatorHighlight.assistant ? 'text-primary' : undefined}>{assistantName || '-'}</span>
								</p>
								<p className='mb-0'>
									<strong>申请时间：</strong>
									{formatDateTime(approval?.createdAt)}
								</p>
							</div>
						</EventModalMediaContent>
					</div>
					<aside className='col-12 col-md-4 col-lg-3 border-top border-md-top-0 border-md-start border-200 p-3'>
						<AppScrollArea className='app-scroll-area--embedded' maxHeight={HISTORY_ASIDE_SCROLL_MAX}>
							{historyLoading ? (
								<div className='d-flex justify-content-center py-3'>
									<Spinner size='sm' />
								</div>
							) : history.length > 0 ? (
								<OrderApprovalHistory history={history} titleRow className='mb-0' />
							) : (
								<p className='mb-0 fs-10 text-500'>暂无审批历史</p>
							)}
						</AppScrollArea>
					</aside>
				</div>
			</Modal.Body>
			<Modal.Footer className='bg-body-tertiary px-x1 border-top-0'>
				<Button variant='falcon-default' size='sm' className='me-auto' onClick={handleViewOrder} disabled={!orderOID || actionBusy}>
					<FontAwesomeIcon icon='eye' className='me-1' />
					查看订单
				</Button>
				<Button variant='falcon-default' size='sm' onClick={onHide} disabled={actionBusy}>
					关闭
				</Button>
				{allowApplicantActions && isRejected ? (
					<Button variant='falcon-default' size='sm' onClick={handleCancelApplication} disabled={actionBusy}>
						<FontAwesomeIcon icon='ban' className='fs-11 me-2' />
						取消申请
					</Button>
				) : null}
				{allowApplicantActions && isRejected && canAdjust ? (
					<Button variant='falcon-primary' size='sm' onClick={() => setShowReapply(true)} disabled={actionBusy}>
						<FontAwesomeIcon icon='history' className='fs-11 me-2' />
						再次申请
					</Button>
				) : null}
				{canDecide ? (
					actionBusy ? (
						<Spinner variant='primary' size='sm' />
					) : (
						<>
							<Button variant='success' size='sm' onClick={handleApprove}>
								<FontAwesomeIcon icon='check' className='me-1' />
								同意
							</Button>
							<Button variant='danger' size='sm' onClick={() => setShowRejectConfirm(true)}>
								<FontAwesomeIcon icon='times' className='me-1' />
								不同意
							</Button>
						</>
					)
				) : null}
			</Modal.Footer>
			<FinalCostReapplyModal
				show={showReapply}
				onHide={() => setShowReapply(false)}
				approval={approval}
				onSubmitted={() => {
					setShowReapply(false);
					onResolved?.({ approvalId, orderId: detail.orderId || '', result: 'resubmitted' });
					onHide();
				}}
			/>
			<OrderDetailModal
				isOpenModal={showOrderDetail}
				setIsOpenModal={setShowOrderDetail}
				className='modal-nested-stack'
				backdropClassName='modal-backdrop-nested'
			/>
			<ApprovalRejectReasonModal
				show={showRejectConfirm}
				onHide={() => setShowRejectConfirm(false)}
				onConfirm={handleReject}
				confirmWaiting={isRejecting}
				hint='驳回后订单上的待审修正会取消，申请人可以重新提交。'
				placeholder='例如：修正后成本与进价不符、原因说明不充分…'
			/>
		</Modal>
	);
};

FinalCostApplyInfoModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func.isRequired,
	approval: PropTypes.object,
	onResolved: PropTypes.func,
	allowDecision: PropTypes.bool,
	allowApplicantActions: PropTypes.bool,
};

export default FinalCostApplyInfoModal;
